/**
 * Is the telephone ringing with the caller this pop is about?
 *
 * The two halves of a call reach the console by different roads and disagree about the
 * number. The pop comes from the API, which normalised it to E.164 — `+359888123456`. The
 * SIP `From` comes from the PBX with whatever the trunk presented, and a GSM gateway
 * presents national format with no `+` — `0888123456`. Comparing the strings finds nothing,
 * which would quietly turn "take the call" back into "fill the form" for every call that
 * arrived over a SIM.
 *
 * So compare the tails. The last nine digits survive every difference that matters here —
 * the leading `+`, a country code, a trunk prefix, the national `0` — and are specific
 * enough that two different callers colliding is not a real concern at the scale of one
 * taxi firm's telephone lines.
 *
 * Short numbers are compared whole. An internal extension or a service number has no tail
 * to speak of, and treating `601` as matching `2601` would answer the wrong call.
 */

/** Below this, a tail is not distinctive enough to trust, so the whole number must match. */
const ShortNumber = 6;

/** How much of the tail to compare. Nine covers a national number without its prefix. */
const TailLength = 9;

function digits(value: string): string {
  return value.replace(/\D/g, '');
}

export function isSameCaller(one: string | null, other: string | null): boolean {
  if (one === null || other === null) return false;

  const a = digits(one);
  const b = digits(other);

  if (a === '' || b === '') return false;
  if (a === b) return true;

  // The shorter number decides how much can be compared: matching nine digits of a
  // fifteen-digit number against a seven-digit one would compare seven against seven
  // anyway, and pretending otherwise only hides that.
  const length = Math.min(a.length, b.length, TailLength);

  if (length < ShortNumber) return false;

  return a.slice(-length) === b.slice(-length);
}

/** Just enough of the telephone's state to decide. */
export interface RingingLine {
  state: string;
  from?: string | null;
}

/**
 * Should taking this caller into the order form also answer the telephone?
 *
 * Only when this browser is the one ringing, and with this caller. The pop goes to every
 * dispatcher in the firm while the dial plan decides whose telephone actually rings, so a
 * desk can see a caller it has no call for — there, taking fills the form and nothing
 * else, exactly as it always did.
 */
export function shouldAnswerOnTake(line: RingingLine, phone: string | null): boolean {
  if (line.state !== 'ringing') return false;

  return isSameCaller(line.from ?? null, phone);
}
